import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Activity, Clock, CheckCircle2, AlertTriangle, TrendingUp, ChevronDown, ChevronRight } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import Modal from '@/components/ui/Modal';
import SideDrawer from '@/components/ui/SideDrawer';
import ChartCard from '@/components/ui/ChartCard';
import EmptyState from '@/components/ui/EmptyState';
import { format, differenceInDays } from 'date-fns';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TimepointResult {
  paramName: string;
  value: number;
  unit: string;
  status: string;
}

interface Timepoint {
  id: string;
  label: string;
  dueDate: string;
  pulledAt?: string;
  status: 'pending' | 'pulled' | 'completed';
  results?: TimepointResult[] | Record<string, number>;
}

interface Study {
  id: string;
  studyCode: string;
  product: string;
  batchNo: string;
  condition: string;
  protocol?: string;
  description?: string;
  specId?: string;
  startDate: string;
  status: string;
  timepoints: Timepoint[];
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const MOCK_STUDIES: Study[] = [
  {
    id: '1', studyCode: 'STB-2024-001', product: 'Amoxicillin 500mg', batchNo: 'BATCH-2024-0042',
    condition: '25°C/60%RH', protocol: 'ICH Q1A(R2)', status: 'active',
    startDate: '2024-01-01T00:00:00Z',
    timepoints: [
      { id: 't1', label: 'T0', dueDate: '2024-01-01T00:00:00Z', pulledAt: '2024-01-01T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 100.1, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 87, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 2.8, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.08, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't2', label: 'T3M', dueDate: '2024-04-01T00:00:00Z', pulledAt: '2024-04-02T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 98.8, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 85, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 3.0, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.10, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't3', label: 'T6M', dueDate: '2024-07-01T00:00:00Z', pulledAt: '2024-07-03T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 97.9, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 83, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 3.2, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.13, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't4', label: 'T12M', dueDate: '2025-01-01T00:00:00Z', pulledAt: '2025-01-04T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 96.5, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 80, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 3.5, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.18, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't5', label: 'T18M', dueDate: '2025-07-01T00:00:00Z', status: 'pulled', results: [] },
      { id: 't6', label: 'T24M', dueDate: '2026-01-01T00:00:00Z', status: 'pending', results: [] },
    ],
  },
  {
    id: '2', studyCode: 'STB-2024-002', product: 'Metformin 500mg', batchNo: 'BATCH-2024-0051',
    condition: '40°C/75%RH', protocol: 'ICH Q1A(R2) Accelerated', status: 'active',
    startDate: '2024-02-01T00:00:00Z',
    timepoints: [
      { id: 't7', label: 'T0', dueDate: '2024-02-01T00:00:00Z', pulledAt: '2024-02-01T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 100.0, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 88, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 1.5, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.05, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't8', label: 'T1M', dueDate: '2024-03-01T00:00:00Z', pulledAt: '2024-03-02T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 99.3, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 85, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 1.7, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.07, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't9', label: 'T3M', dueDate: '2024-05-01T00:00:00Z', pulledAt: '2024-05-02T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 97.8, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 82, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 2.1, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.11, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't10', label: 'T6M', dueDate: '2024-08-01T00:00:00Z', status: 'pending', results: [] },
    ],
  },
  {
    id: '3', studyCode: 'STB-2023-001', product: 'Ibuprofen 400mg', batchNo: 'BATCH-2023-0030',
    condition: '25°C/60%RH', protocol: 'ICH Q1A(R2)', status: 'completed',
    startDate: '2021-06-01T00:00:00Z',
    timepoints: [
      { id: 't11', label: 'T0', dueDate: '2021-06-01T00:00:00Z', pulledAt: '2021-06-01T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 100.4, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 89, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.4, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.06, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't12', label: 'T3M', dueDate: '2021-09-01T00:00:00Z', pulledAt: '2021-09-02T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 99.7, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 87, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.5, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.08, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't13', label: 'T6M', dueDate: '2021-12-01T00:00:00Z', pulledAt: '2021-12-03T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 99.1, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 85, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.6, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.10, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't14', label: 'T12M', dueDate: '2022-06-01T00:00:00Z', pulledAt: '2022-06-02T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 98.5, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 83, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.7, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.13, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't15', label: 'T18M', dueDate: '2022-12-01T00:00:00Z', pulledAt: '2022-12-04T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 97.9, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 81, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.8, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.17, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't16', label: 'T24M', dueDate: '2023-06-01T00:00:00Z', pulledAt: '2023-06-05T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 97.2, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 79, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.9, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.20, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
    ],
  },
  // ── 9 additional studies ───────────────────────────────────────────────────
  {
    id: '4', studyCode: 'STB-2022-001', product: 'Paracetamol 650mg', batchNo: 'BATCH-2022-0018',
    condition: '25°C/60%RH', protocol: 'ICH Q1A(R2)', status: 'completed',
    startDate: '2022-03-01T00:00:00Z',
    timepoints: [
      { id: 't17', label: 'T0', dueDate: '2022-03-01T00:00:00Z', pulledAt: '2022-03-01T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 99.8, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 91, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.3, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.04, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't18', label: 'T3M', dueDate: '2022-06-01T00:00:00Z', pulledAt: '2022-06-02T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 99.4, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 90, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.4, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.05, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't19', label: 'T6M', dueDate: '2022-09-01T00:00:00Z', pulledAt: '2022-09-03T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 99.0, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 88, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.5, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.06, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't20', label: 'T12M', dueDate: '2023-03-01T00:00:00Z', pulledAt: '2023-03-04T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 98.3, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 86, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.6, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.09, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't21', label: 'T18M', dueDate: '2023-09-01T00:00:00Z', pulledAt: '2023-09-02T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 97.8, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 84, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.7, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.12, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't22', label: 'T24M', dueDate: '2024-03-01T00:00:00Z', pulledAt: '2024-03-05T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 97.1, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 82, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.8, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.15, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
    ],
  },
  {
    // Downward trend study — demo purpose
    id: '5', studyCode: 'STB-2023-002', product: 'Atorvastatin 10mg', batchNo: 'BATCH-2023-0044',
    condition: '40°C/75%RH', protocol: 'ICH Q1A(R2) Accelerated', status: 'completed',
    startDate: '2023-01-15T00:00:00Z',
    timepoints: [
      { id: 't23', label: 'T0', dueDate: '2023-01-15T00:00:00Z', pulledAt: '2023-01-15T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 100.2, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 92, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.9, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.07, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't24', label: 'T1M', dueDate: '2023-02-15T00:00:00Z', pulledAt: '2023-02-16T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 98.5, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 88, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 1.1, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.12, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't25', label: 'T3M', dueDate: '2023-04-15T00:00:00Z', pulledAt: '2023-04-17T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 95.3, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 82, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 1.5, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.21, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't26', label: 'T6M', dueDate: '2023-07-15T00:00:00Z', pulledAt: '2023-07-18T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 91.8, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 76, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 2.0, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.33, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
    ],
  },
  {
    id: '6', studyCode: 'STB-2023-003', product: 'Ciprofloxacin 250mg', batchNo: 'BATCH-2023-0062',
    condition: '30°C/65%RH', protocol: 'ICH Q1A(R2) Intermediate', status: 'completed',
    startDate: '2023-04-01T00:00:00Z',
    timepoints: [
      { id: 't27', label: 'T0', dueDate: '2023-04-01T00:00:00Z', pulledAt: '2023-04-01T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 100.6, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 90, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 2.1, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.09, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't28', label: 'T6M', dueDate: '2023-10-01T00:00:00Z', pulledAt: '2023-10-03T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 99.2, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 88, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 2.3, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.12, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't29', label: 'T12M', dueDate: '2024-04-01T00:00:00Z', pulledAt: '2024-04-02T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 98.1, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 86, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 2.5, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.16, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
    ],
  },
  {
    id: '7', studyCode: 'STB-2023-004', product: 'Sodium Chloride 0.9%', batchNo: 'BATCH-2023-0075',
    condition: '5°C±3°C', protocol: 'ICH Q1A(R2)', status: 'active',
    startDate: '2023-07-01T00:00:00Z',
    timepoints: [
      { id: 't30', label: 'T0', dueDate: '2023-07-01T00:00:00Z', pulledAt: '2023-07-01T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 100.0, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.0, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.01, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't31', label: 'T6M', dueDate: '2024-01-01T00:00:00Z', pulledAt: '2024-01-03T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 99.9, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.0, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.01, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't32', label: 'T12M', dueDate: '2024-07-01T00:00:00Z', pulledAt: '2024-07-04T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 99.8, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.0, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.02, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't33', label: 'T18M', dueDate: '2025-01-01T00:00:00Z', status: 'pulled', results: [] },
      { id: 't34', label: 'T24M', dueDate: '2025-07-01T00:00:00Z', status: 'pending', results: [] },
      { id: 't35', label: 'T36M', dueDate: '2026-07-01T00:00:00Z', status: 'pending', results: [] },
    ],
  },
  {
    id: '8', studyCode: 'STB-2024-003', product: 'Prednisolone 5mg', batchNo: 'BATCH-2024-0088',
    condition: '25°C/60%RH', protocol: 'ICH Q1A(R2)', status: 'active',
    startDate: '2024-03-01T00:00:00Z',
    timepoints: [
      { id: 't36', label: 'T0', dueDate: '2024-03-01T00:00:00Z', pulledAt: '2024-03-01T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 100.3, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 93, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 1.2, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.06, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't37', label: 'T3M', dueDate: '2024-06-01T00:00:00Z', pulledAt: '2024-06-03T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 99.7, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 91, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 1.3, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.08, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't38', label: 'T6M', dueDate: '2024-09-01T00:00:00Z', status: 'pulled', results: [] },
      { id: 't39', label: 'T12M', dueDate: '2025-03-01T00:00:00Z', status: 'pending', results: [] },
      { id: 't40', label: 'T18M', dueDate: '2025-09-01T00:00:00Z', status: 'pending', results: [] },
      { id: 't41', label: 'T24M', dueDate: '2026-03-01T00:00:00Z', status: 'pending', results: [] },
    ],
  },
  {
    id: '9', studyCode: 'STB-2022-002', product: 'Azithromycin 500mg', batchNo: 'BATCH-2022-0033',
    condition: '25°C/60%RH', protocol: 'ICH Q1A(R2)', status: 'completed',
    startDate: '2022-06-01T00:00:00Z',
    timepoints: [
      { id: 't42', label: 'T0', dueDate: '2022-06-01T00:00:00Z', pulledAt: '2022-06-01T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 100.1, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 86, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 3.5, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.10, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't43', label: 'T3M', dueDate: '2022-09-01T00:00:00Z', pulledAt: '2022-09-02T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 99.4, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 84, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 3.7, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.14, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't44', label: 'T6M', dueDate: '2022-12-01T00:00:00Z', pulledAt: '2022-12-03T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 98.6, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 82, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 4.0, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.18, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't45', label: 'T12M', dueDate: '2023-06-01T00:00:00Z', pulledAt: '2023-06-04T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 97.8, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 80, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 4.3, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.23, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't46', label: 'T18M', dueDate: '2023-12-01T00:00:00Z', pulledAt: '2023-12-05T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 97.0, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 78, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 4.6, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.28, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't47', label: 'T24M', dueDate: '2024-06-01T00:00:00Z', pulledAt: '2024-06-06T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 96.3, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 76, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 4.9, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.33, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
    ],
  },
  {
    id: '10', studyCode: 'STB-2024-004', product: 'Pantoprazole 40mg', batchNo: 'BATCH-2024-0101',
    condition: '25°C/60%RH', protocol: 'ICH Q1A(R2)', status: 'active',
    startDate: '2024-06-01T00:00:00Z',
    timepoints: [
      { id: 't48', label: 'T0', dueDate: '2024-06-01T00:00:00Z', pulledAt: '2024-06-01T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 100.0, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 94, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.6, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.05, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't49', label: 'T3M', dueDate: '2024-09-01T00:00:00Z', pulledAt: '2024-09-03T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 99.5, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 92, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.7, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.07, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't50', label: 'T6M', dueDate: '2024-12-01T00:00:00Z', pulledAt: '2024-12-04T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 99.0, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 91, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 0.8, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.09, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't51', label: 'T12M', dueDate: '2025-06-01T00:00:00Z', status: 'pending', results: [] },
      { id: 't52', label: 'T18M', dueDate: '2025-12-01T00:00:00Z', status: 'pending', results: [] },
      { id: 't53', label: 'T24M', dueDate: '2026-06-01T00:00:00Z', status: 'pending', results: [] },
    ],
  },
  {
    id: '11', studyCode: 'STB-2021-001', product: 'Metformin 500mg', batchNo: 'BATCH-2021-0007',
    condition: '40°C/75%RH', protocol: 'ICH Q1A(R2) Accelerated', status: 'on_hold',
    startDate: '2021-09-01T00:00:00Z',
    timepoints: [
      { id: 't54', label: 'T0', dueDate: '2021-09-01T00:00:00Z', pulledAt: '2021-09-01T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 100.2, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 87, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 1.4, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.06, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't55', label: 'T1M', dueDate: '2021-10-01T00:00:00Z', pulledAt: '2021-10-02T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 99.4, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 85, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 1.6, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.09, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't56', label: 'T3M', dueDate: '2021-12-01T00:00:00Z', status: 'pending', results: [] },
      { id: 't57', label: 'T6M', dueDate: '2022-03-01T00:00:00Z', status: 'pending', results: [] },
    ],
  },
  {
    id: '12', studyCode: 'STB-2025-001', product: 'Amoxicillin 500mg', batchNo: 'BATCH-2025-0011',
    condition: '30°C/65%RH', protocol: 'ICH Q1A(R2) Intermediate', status: 'discontinued',
    startDate: '2025-01-15T00:00:00Z',
    timepoints: [
      { id: 't58', label: 'T0', dueDate: '2025-01-15T00:00:00Z', pulledAt: '2025-01-15T00:00:00Z', status: 'completed', results: [{ paramName: 'Assay', value: 100.5, unit: '%', status: 'pass' }, { paramName: 'Dissolution', value: 88, unit: '%', status: 'pass' }, { paramName: 'Water Content', value: 2.9, unit: '%', status: 'pass' }, { paramName: 'Related Substances', value: 0.07, unit: '%', status: 'pass' }, { paramName: 'Appearance', value: 1, unit: 'conform', status: 'pass' }] },
      { id: 't59', label: 'T3M', dueDate: '2025-04-15T00:00:00Z', status: 'pending', results: [] },
      { id: 't60', label: 'T6M', dueDate: '2025-07-15T00:00:00Z', status: 'pending', results: [] },
    ],
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getTimepointStatusColor(status: string) {
  if (status === 'completed') return { dot: 'bg-green-500', ring: 'ring-green-300', text: 'text-green-700' };
  if (status === 'pulled') return { dot: 'bg-amber-500', ring: 'ring-amber-300', text: 'text-amber-700' };
  return { dot: 'bg-gray-300', ring: 'ring-gray-200', text: 'text-text-dim' };
}

function getTimepointProgress(timepoints: Timepoint[]): string {
  if (!Array.isArray(timepoints) || timepoints.length === 0) return '0/0';
  const done = timepoints.filter((t) => t.status === 'completed').length;
  return `${done}/${timepoints.length} complete`;
}

// ─── Stat Card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon: Icon, color, highlight }: { label: string; value: number; icon: React.ElementType; color: string; highlight?: boolean }) {
  return (
    <div className={`card p-4 flex items-center gap-4 ${highlight && value > 0 ? 'border border-amber-200' : ''}`}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${color}18` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div>
        <p className={`text-2xl font-bold font-serif ${highlight && value > 0 ? 'text-amber-600' : 'text-text'}`}>{value}</p>
        <p className="text-xs text-text-muted">{label}</p>
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function StabilityStudies() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedStudy, setSelectedStudy] = useState<Study | null>(null);
  const [expandedTimepoint, setExpandedTimepoint] = useState<string | null>(null);
  const [showEnterTP, setShowEnterTP] = useState<Timepoint | null>(null);
  const [tpResults, setTpResults] = useState<Record<string, string>>({});

  const [form, setForm] = useState({
    product: '', batchNo: '', condition: '', startDate: '', protocol: '', description: '', specId: '',
  });

  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['stability-studies', { search, status: statusFilter, page }],
    queryFn: () =>
      api.get('/stability', { params: { search, status: statusFilter, page, limit: 20 } })
        .then((r) => r.data)
        .catch(() => ({ data: MOCK_STUDIES, meta: { total: MOCK_STUDIES.length } })),
  });

  const rows: Study[] = (Array.isArray(data?.data) && data.data.length > 0) ? data.data : MOCK_STUDIES;
  const total: number = data?.meta?.total ?? rows.length;

  const now = new Date();
  const pendingPulls = rows.reduce((acc, s) => {
    if (s.status !== 'active') return acc;
    return acc + (Array.isArray(s.timepoints) ? s.timepoints.filter((t) => {
      if (t.status !== 'pending') return false;
      const days = differenceInDays(new Date(t.dueDate), now);
      return days >= 0 && days <= 7;
    }).length : 0);
  }, 0);

  const stats = {
    active: rows.filter((s) => s.status === 'active').length,
    pendingPulls,
    completed: rows.filter((s) => s.status === 'completed').length,
    totalTimepoints: rows.reduce((acc, s) => acc + (Array.isArray(s.timepoints) ? s.timepoints.length : 0), 0),
  };

  const createMutation = useMutation({
    mutationFn: (payload: typeof form) => api.post('/stability', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stability-studies'] });
      setShowCreate(false);
      setForm({ product: '', batchNo: '', condition: '', startDate: '', protocol: '', description: '', specId: '' });
    },
  });

  const saveTPResultsMutation = useMutation({
    mutationFn: ({ studyId, timepointId, results }: { studyId: string; timepointId: string; results: Record<string, string> }) =>
      api.put(`/stability/${studyId}/timepoints/${timepointId}`, { results }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stability-studies'] });
      setShowEnterTP(null);
      setTpResults({});
    },
  });

  // Build trend chart data — results may be array [{paramName,value}] or JSON object {Assay: 99.3}
  const normaliseResults = (results: TimepointResult[] | Record<string, number> | undefined): Record<string, number> => {
    if (!results) return {};
    if (Array.isArray(results)) {
      return Object.fromEntries(results.map((r) => [r.paramName, Number(r.value) || 0]));
    }
    if (typeof results === 'object') return results as Record<string, number>;
    return {};
  };

  const trendData = React.useMemo(() => {
    if (!selectedStudy) return [];
    return (Array.isArray(selectedStudy.timepoints) ? selectedStudy.timepoints : [])
      .filter((t) => t.status === 'completed' && t.results && Object.keys(normaliseResults(t.results)).length > 0)
      .map((t) => ({ label: t.label, ...normaliseResults(t.results) }));
  }, [selectedStudy]);

  // Get unique parameter names for trend chart
  const trendParams = React.useMemo(() => {
    if (!selectedStudy) return [];
    const names = new Set<string>();
    (Array.isArray(selectedStudy.timepoints) ? selectedStudy.timepoints : [])
      .forEach((t) => Object.keys(normaliseResults(t.results)).forEach((k) => names.add(k)));
    return Array.from(names);
  }, [selectedStudy]);

  const CHART_COLORS = ['#b07d1a', '#2563EB', '#16A34A', '#DC2626', '#7C3AED'];

  const columns: Column<Record<string, unknown>>[] = [
    {
      key: 'studyCode', label: 'Study Code', width: 'w-36',
      render: (row) => <span className="font-mono text-xs text-text-muted">{String(row.studyCode)}</span>,
    },
    {
      key: 'product', label: 'Product / Batch',
      render: (row) => (
        <div>
          <p className="font-medium text-text text-sm">{String(row.product)}</p>
          <p className="text-xs text-text-dim font-mono">{String(row.batchNo)}</p>
        </div>
      ),
    },
    {
      key: 'condition', label: 'Condition', width: 'w-32',
      render: (row) => (
        <span className="text-xs font-mono px-2 py-0.5 rounded-md bg-teal/10 text-teal-dark">{String(row.condition)}</span>
      ),
    },
    {
      key: 'startDate', label: 'Start Date', width: 'w-28',
      render: (row) => (
        <span className="text-xs font-mono text-text-muted">
          {row.startDate ? format(new Date(String(row.startDate)), 'd MMM yyyy') : '—'}
        </span>
      ),
    },
    {
      key: 'status', label: 'Status', width: 'w-28',
      render: (row) => <PillBadge value={String(row.status)} showDot />,
    },
    {
      key: 'timepoints', label: 'Timepoint Progress',
      render: (row) => {
        const tps = row.timepoints as Timepoint[];
        return (
          <span className="text-xs text-text">{getTimepointProgress(Array.isArray(tps) ? tps : [])}</span>
        );
      },
    },
  ];

  return (
    <div className="space-y-5">
      <PageHeader
        title="Stability Studies"
        subtitle={`${total} studies tracked`}
        actions={
          <button onClick={() => setShowCreate(true)} className="btn-primary">
            <Plus size={13} /> New Study
          </button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Active Studies" value={stats.active} icon={Activity} color="#b07d1a" />
        <StatCard label="Pending Pulls (7d)" value={stats.pendingPulls} icon={AlertTriangle} color="#D97706" highlight />
        <StatCard label="Completed" value={stats.completed} icon={CheckCircle2} color="#16A34A" />
        <StatCard label="Total Timepoints" value={stats.totalTimepoints} icon={Clock} color="#2563EB" />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <input
            placeholder="Search by product, study code..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="input pl-8"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="select w-36"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <DataTable
        columns={columns}
        data={rows as unknown as Record<string, unknown>[]}
        loading={isLoading}
        onRowClick={(row) => { setSelectedStudy(row as unknown as Study); setExpandedTimepoint(null); }}
        total={total}
        page={page}
        pageSize={20}
        onPageChange={setPage}
      />

      {/* Study Detail SideDrawer */}
      <SideDrawer
        isOpen={!!selectedStudy}
        onClose={() => { setSelectedStudy(null); setExpandedTimepoint(null); }}
        title={selectedStudy?.studyCode || ''}
        subtitle={`${selectedStudy?.product} · ${selectedStudy?.condition}`}
        width={640}
      >
        {selectedStudy && (
          <div className="space-y-5">
            {/* Study Info */}
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Status', value: <PillBadge value={selectedStudy.status} showDot /> },
                { label: 'Condition', value: selectedStudy.condition },
                { label: 'Batch No.', value: selectedStudy.batchNo },
                { label: 'Start Date', value: format(new Date(selectedStudy.startDate), 'd MMM yyyy') },
                { label: 'Protocol', value: selectedStudy.protocol || '—' },
              ].map(({ label, value }) => (
                <div key={label} className="p-3 bg-bg rounded-lg">
                  <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">{label}</p>
                  <div className="text-xs font-medium text-text">{typeof value === 'string' ? value : value}</div>
                </div>
              ))}
            </div>

            {selectedStudy.description && (
              <div className="p-3 bg-bg rounded-lg">
                <p className="text-[10px] uppercase font-semibold text-text-dim mb-1">Description</p>
                <p className="text-sm text-text">{selectedStudy.description}</p>
              </div>
            )}

            {/* Timepoints Timeline */}
            <div>
              <p className="text-xs font-semibold text-text-muted mb-3 flex items-center gap-1.5">
                <Clock size={12} className="text-teal" /> Timepoints
              </p>
              <div className="space-y-2">
                {!Array.isArray(selectedStudy.timepoints) || selectedStudy.timepoints.length === 0 ? (
                  <EmptyState icon="data" title="No timepoints" description="This study has no timepoints configured." />
                ) : (
                  selectedStudy.timepoints.map((tp) => {
                    const colors = getTimepointStatusColor(tp.status);
                    const isExpanded = expandedTimepoint === tp.id;
                    const daysToSchedule = differenceInDays(new Date(tp.dueDate), now);
                    const isDue = tp.status === 'pending' && daysToSchedule >= 0 && daysToSchedule <= 7;

                    return (
                      <div key={tp.id} className={`rounded-lg border ${isDue ? 'border-amber-300 bg-amber-50/30' : 'border-border'} overflow-hidden`}>
                        <button
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-bg/60 transition-colors"
                          onClick={() => setExpandedTimepoint(isExpanded ? null : tp.id)}
                        >
                          <span className={`w-3 h-3 rounded-full flex-shrink-0 ring-2 ${colors.dot} ${colors.ring}`} />
                          <span className="text-sm font-semibold text-text flex-1 text-left">{tp.label}</span>
                          <span className="text-xs text-text-muted font-mono">
                            {format(new Date(tp.dueDate), 'd MMM yyyy')}
                          </span>
                          {isDue && (
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500 text-white">Due Soon</span>
                          )}
                          <PillBadge value={tp.status} size="xs" />
                          {isExpanded ? <ChevronDown size={14} className="text-text-dim flex-shrink-0" /> : <ChevronRight size={14} className="text-text-dim flex-shrink-0" />}
                        </button>

                        {isExpanded && (
                          <div className="px-3 pb-3 border-t border-border/50 pt-2">
                            {tp.pulledAt && (
                              <p className="text-xs text-text-muted mb-2">
                                Pulled: <span className="font-medium text-text">{format(new Date(tp.pulledAt), 'd MMM yyyy')}</span>
                              </p>
                            )}
                            {Object.keys(normaliseResults(tp.results)).length > 0 ? (
                              <div className="space-y-1">
                                {Object.entries(normaliseResults(tp.results)).map(([param, val]) => (
                                  <div key={param} className="flex items-center justify-between text-xs px-2 py-1.5 bg-bg rounded">
                                    <span className="text-text font-medium">{param}</span>
                                    <span className="font-mono text-text">{(Number(val) || 0).toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="flex items-center justify-between">
                                <p className="text-xs text-text-dim">No results recorded.</p>
                                {tp.status !== 'pending' && (
                                  <button
                                    onClick={() => { setShowEnterTP(tp); setTpResults({}); }}
                                    className="text-xs btn-secondary py-1 px-2"
                                  >
                                    Enter Results
                                  </button>
                                )}
                              </div>
                            )}
                            {tp.status === 'pending' && (
                              <p className="text-xs text-text-dim mt-1">
                                Sample pull scheduled for {format(new Date(tp.dueDate), 'd MMM yyyy')}
                                {daysToSchedule > 0 ? ` (${daysToSchedule}d away)` : daysToSchedule === 0 ? ' (today)' : ''}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Trend Chart */}
            {trendData.length >= 2 && trendParams.length > 0 && (
              <ChartCard
                title="Stability Trend"
                subtitle="Parameter values over time"
                height={220}
              >
                <ResponsiveContainer>
                  <LineChart data={trendData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#C6D8D8" strokeOpacity={0.5} />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#6899A0' }} />
                    <YAxis tick={{ fontSize: 10, fill: '#6899A0' }} width={35} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                    <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    {trendParams.map((param, idx) => (
                      <Line
                        key={param}
                        type="monotone"
                        dataKey={param}
                        stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                        strokeWidth={2}
                        dot={{ r: 4, fill: CHART_COLORS[idx % CHART_COLORS.length] }}
                        activeDot={{ r: 6 }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
          </div>
        )}
      </SideDrawer>

      {/* Enter Timepoint Results Modal */}
      <Modal
        isOpen={!!showEnterTP}
        onClose={() => { setShowEnterTP(null); setTpResults({}); }}
        title={`Enter Results — ${showEnterTP?.label}`}
        subtitle={selectedStudy ? `Study: ${selectedStudy.studyCode}` : ''}
        size="md"
        footer={
          <>
            <button onClick={() => { setShowEnterTP(null); setTpResults({}); }} className="btn-secondary">Cancel</button>
            <button
              onClick={() => {
                if (selectedStudy && showEnterTP) {
                  saveTPResultsMutation.mutate({
                    studyId: selectedStudy.id,
                    timepointId: showEnterTP.id,
                    results: tpResults,
                  });
                }
              }}
              disabled={saveTPResultsMutation.isPending}
              className="btn-primary"
            >
              {saveTPResultsMutation.isPending ? 'Saving...' : 'Save Results'}
            </button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-xs text-text-muted">Enter measured values for timepoint <span className="font-semibold">{showEnterTP?.label}</span>.</p>
          {['Assay', 'Dissolution', 'Water Content', 'Related Substances'].map((param) => (
            <div key={param}>
              <label className="label">{param}</label>
              <input
                type="number"
                step="any"
                className="input"
                value={tpResults[param] || ''}
                onChange={(e) => setTpResults((r) => ({ ...r, [param]: e.target.value }))}
                placeholder={`Enter ${param} value...`}
              />
            </div>
          ))}
        </div>
      </Modal>

      {/* Create Study Modal */}
      <Modal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="New Stability Study"
        subtitle="Create a new stability study protocol"
        size="lg"
        footer={
          <>
            <button onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
            <button
              onClick={() => createMutation.mutate(form)}
              disabled={createMutation.isPending || !form.product || !form.batchNo || !form.condition || !form.startDate}
              className="btn-primary"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Study'}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Product *</label>
            <input
              className="input"
              value={form.product}
              onChange={(e) => setForm((f) => ({ ...f, product: e.target.value }))}
              placeholder="e.g. Amoxicillin 500mg"
            />
          </div>
          <div>
            <label className="label">Batch No. *</label>
            <input
              className="input"
              value={form.batchNo}
              onChange={(e) => setForm((f) => ({ ...f, batchNo: e.target.value }))}
              placeholder="e.g. BATCH-2024-0042"
            />
          </div>
          <div>
            <label className="label">Storage Condition *</label>
            <select
              className="select"
              value={form.condition}
              onChange={(e) => setForm((f) => ({ ...f, condition: e.target.value }))}
            >
              <option value="">Select condition...</option>
              <option value="25°C/60%RH">25°C/60%RH (Long-term)</option>
              <option value="30°C/65%RH">30°C/65%RH (Intermediate)</option>
              <option value="40°C/75%RH">40°C/75%RH (Accelerated)</option>
              <option value="-20°C">-20°C (Frozen)</option>
              <option value="2-8°C">2–8°C (Refrigerated)</option>
            </select>
          </div>
          <div>
            <label className="label">Start Date *</label>
            <input
              type="date"
              className="input"
              value={form.startDate}
              onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
            />
          </div>
          <div className="col-span-2">
            <label className="label">Protocol</label>
            <input
              className="input"
              value={form.protocol}
              onChange={(e) => setForm((f) => ({ ...f, protocol: e.target.value }))}
              placeholder="e.g. ICH Q1A(R2)"
            />
          </div>
          <div className="col-span-2">
            <label className="label">Description</label>
            <textarea
              className="input resize-none"
              rows={2}
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Study scope and objectives..."
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}
